import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { ChevronDown, Download, Maximize2, Minimize2 } from 'lucide-react';
import { fetchCollegeData } from '../helpers/collegeAPI';
import { smartSearchCollegeDatabase } from '../helpers/searchCollegeDatabase';
import { getAutocompleteSuggestions } from '../helpers/smartSearch';
import { useDashboardState } from '../helpers/useDashboardStorage';
import ColumnPicker from '../components/ColumnPicker/ColumnPicker';
import { CATEGORY_OPTIONS, DEFAULT_CUSTOM_COLUMNS, compareColumnSortValues, createCustomColumn, formatValue, getColDef, getRawSortValue } from '../helpers/collegeColumns';
import { lookupFafsaCode } from '../helpers/fafsaLookup';
import { createXlsxFile } from '../helpers/xlsxExport';
import './CollegeListPage.css';

const STARTER_COLLEGE_EXAMPLES = [
  'University of California-Los Angeles',
  'Rutgers University-New Brunswick',
  'Howard University',
];
const TOUR_SEARCH_USED_EVENT = 'maicolleges:tour-college-search-used';
const TOUR_COLLEGE_ADDED_EVENT = 'maicolleges:tour-college-added';

function dispatchTourEvent(eventName) {
  window.dispatchEvent(new CustomEvent(eventName));
}

function normalizeLinkHref(value) {
  const trimmed = String(value || '').trim();
  if (!trimmed) return '';
  if (/^(https?:\/\/|mailto:|tel:)/i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function escapeCsv(value) {
  const text = String(value ?? '');
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function downloadFile(content, fileName, mimeType) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function CollegeListPage({ dashboardName }) {
  // Get the dashboardId from URL params for per-dashboard storage
  const { dashboardId } = useParams();
  const storageId = dashboardId || 'home';

  const [collegeData, setCollegeData] = useState(null);
  const [selectedSchool, setSelectedSchool] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [schoolInfo, setSchoolInfo] = useState(null);
  const [collegeLoading, setCollegeLoading] = useState(true);
  const [collegeLoadError, setCollegeLoadError] = useState(false);

  // Active columns — persisted per dashboard
  const [activeColumns, setActiveColumns] = useDashboardState(storageId, 'activeColumns', ['school_name']);
  const [customColumns, setCustomColumns] = useDashboardState(storageId, 'customColumns', DEFAULT_CUSTOM_COLUMNS);

  // Rows — persisted per dashboard
  const [rows, setRows] = useDashboardState(storageId, 'rows', []);

  // Sort state — persisted per dashboard
  const [sortConfig, setSortConfig] = useDashboardState(storageId, 'sortConfig', null);
  const [columnFilters, setColumnFilters] = useDashboardState(storageId, 'columnFilters', {});
  const [columnWidths, setColumnWidths] = useDashboardState(storageId, 'columnWidths', {});
  const [frozenSchoolName, setFrozenSchoolName] = useDashboardState(storageId, 'frozenSchoolName', false);
  const [tableExpanded, setTableExpanded] = useDashboardState(storageId, 'tableExpanded', false);
  const [exportOpen, setExportOpen] = useState(false);

  // Column picker
  const [columnPickerOpen, setColumnPickerOpen] = useState(false);
  const [columnSearch, setColumnSearch] = useState('');
  const pickerRef = useRef(null);
  const [columnPickerPos, setColumnPickerPos] = useState(null);
  const exportRef = useRef(null);
  const [headerMenuCol, setHeaderMenuCol] = useState(null);
  const [headerMenuPosition, setHeaderMenuPosition] = useState(null);
  const [renamingCol, setRenamingCol] = useState(null);
  const [renameDraft, setRenameDraft] = useState('');

  // Drag-and-drop column reorder
  const [draggedCol, setDraggedCol] = useState(null);
  const [dropTargetCol, setDropTargetCol] = useState(null);
  const resizeStateRef = useRef(null);

  // Inline cell editing
  const [editingCell, setEditingCell] = useState(null); // { rowId, colId }
  const [editCellValue, setEditCellValue] = useState('');
  const cellInputRef = useRef(null);
  const [customTextPreview, setCustomTextPreview] = useState(null);

  // Debounced search results
  const [collegeNamesList, setCollegeNamesList] = useState([]);
  const debounceRef = useRef(null);
  const [schoolActionMessage, setSchoolActionMessage] = useState('');

  const getLabelSelectClass = (value) => {
    if (!value) return 'custom-label-select';
    const tone = Array.from(value).reduce((sum, char) => sum + char.charCodeAt(0), 0) % 6;
    return `custom-label-select custom-label-select--set custom-label-select--tone-${tone}`;
  };

  // Close picker on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setColumnPickerOpen(false);
      }
      if (exportRef.current && !exportRef.current.contains(e.target)) {
        setExportOpen(false);
      }
      if (!e.target.closest('.column-header-menu') && !e.target.closest('.column-menu-trigger')) {
        setHeaderMenuCol(null);
        setHeaderMenuPosition(null);
        setRenamingCol(null);
        setRenameDraft('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!headerMenuCol) return undefined;

    const closeFloatingMenu = () => {
      setHeaderMenuCol(null);
      setHeaderMenuPosition(null);
      setRenamingCol(null);
      setRenameDraft('');
    };

    window.addEventListener('resize', closeFloatingMenu);
    window.addEventListener('scroll', closeFloatingMenu, true);
    return () => {
      window.removeEventListener('resize', closeFloatingMenu);
      window.removeEventListener('scroll', closeFloatingMenu, true);
    };
  }, [headerMenuCol]);

  useEffect(() => {
    if (!customTextPreview) return undefined;

    const hidePreview = () => setCustomTextPreview(null);
    window.addEventListener('resize', hidePreview);
    window.addEventListener('scroll', hidePreview, true);

    return () => {
      window.removeEventListener('resize', hidePreview);
      window.removeEventListener('scroll', hidePreview, true);
    };
  }, [customTextPreview]);

  useEffect(() => {
    if (!columnPickerOpen) return undefined;
    const close = (event) => {
      if (pickerRef.current?.contains(event.target)) return;
      setColumnPickerOpen(false);
    };
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [columnPickerOpen]);

  useEffect(() => {
    if (!frozenSchoolName || activeColumns[0] === 'school_name' || !activeColumns.includes('school_name')) {
      return;
    }

    setActiveColumns(prev => ['school_name', ...prev.filter(id => id !== 'school_name')]);
  }, [activeColumns, frozenSchoolName, setActiveColumns]);

  // Fetch college data on mount
  useEffect(() => {
    let cancelled = false;
    setCollegeLoading(true);
    setCollegeLoadError(false);

    fetchCollegeData()
      .then((data) => {
        if (cancelled) return;
        if (!data?.results) {
          setCollegeData({ results: [] });
          setCollegeLoadError(true);
          return;
        }
        setCollegeData(data);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('Error fetching college data:', error);
        setCollegeData({ results: [] });
        setCollegeLoadError(true);
      })
      .finally(() => {
        if (!cancelled) {
          setCollegeLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // Debounced smart search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const resultsArray = collegeData?.results ?? [];

    if (!inputValue || inputValue.length < 1 || resultsArray.length === 0) {
      setCollegeNamesList([]);
      return;
    }

    debounceRef.current = setTimeout(() => {
      const suggestions = getAutocompleteSuggestions(inputValue, resultsArray, 12);
      setCollegeNamesList(suggestions);
    }, 150);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [inputValue, collegeData]);

  // Fetch school info when selection changes
  useEffect(() => {
    if (selectedSchool && collegeData) {
      const fetchInfo = async () => {
        try {
          const data = await smartSearchCollegeDatabase(selectedSchool, collegeData);
          setSchoolInfo(data);
        } catch (error) {
          console.error('Error fetching school info:', error);
        }
      };
      fetchInfo();
    }
  }, [selectedSchool, collegeData]);

  // ---- Column management ----

  const toggleColumn = (colId) => {
    setActiveColumns(prev => {
      if (prev.includes(colId)) {
        if (sortConfig && sortConfig.columnId === colId) {
          setSortConfig(null);
        }
        return prev.filter(id => id !== colId);
      } else {
        return [...prev, colId];
      }
    });
  };

  useEffect(() => {
    setCustomColumns(prev => {
      const existingIds = new Set(prev.map((column) => column.id));
      const missingDefaults = DEFAULT_CUSTOM_COLUMNS.filter((column) => !existingIds.has(column.id));
      return missingDefaults.length ? [...missingDefaults, ...prev] : prev;
    });
  }, [setCustomColumns]);

  const updateCustomField = (rowId, colId, value) => {
    setRows(prev => prev.map(row => (
      row.id === rowId
        ? { ...row, customFields: { ...(row.customFields || {}), [colId]: value } }
        : row
    )));
  };

  const handleCreateCustomColumn = (label, customType = 'text', labels = []) => {
    const existingColumn = customColumns.find(
      (column) => column.label.trim().toLowerCase() === label.trim().toLowerCase()
    );

    if (existingColumn) {
      setActiveColumns(prev => (prev.includes(existingColumn.id) ? prev : [...prev, existingColumn.id]));
      return;
    }

    const newColumn = createCustomColumn(label, customType, labels);
    if (!newColumn) return;

    setCustomColumns(prev => [...prev, newColumn]);
    setActiveColumns(prev => [...prev, newColumn.id]);
  };

  const startRenameCustomColumn = (colId) => {
    const column = customColumns.find(col => col.id === colId);
    if (!column) return;
    setRenamingCol(colId);
    setRenameDraft(column.label);
  };

  const saveRenameCustomColumn = (colId) => {
    const nextLabel = renameDraft.trim().replace(/\s+/g, ' ');
    if (!nextLabel) return;

    setCustomColumns(prev => prev.map(col => (
      col.id === colId ? { ...col, label: nextLabel } : col
    )));
    setRenamingCol(null);
    setRenameDraft('');
    setHeaderMenuCol(null);
    setHeaderMenuPosition(null);
  };

  const handleRemoveColumnFromTable = (colId) => {
    setActiveColumns(prev => prev.filter(id => id !== colId));
    setColumnFilters(prev => {
      const next = { ...prev };
      delete next[colId];
      return next;
    });
    setColumnWidths(prev => {
      const next = { ...prev };
      delete next[colId];
      return next;
    });
    if (sortConfig?.columnId === colId) setSortConfig(null);
    setRenamingCol(null);
    setRenameDraft('');
    setHeaderMenuCol(null);
    setHeaderMenuPosition(null);
  };

  const handleDeleteCustomColumn = (colId) => {
    const colDef = getColDef(colId, customColumns);
    if (!colDef || colDef.type !== 'custom' || colDef.isBuiltIn) return;

    setCustomColumns(prev => prev.filter(col => col.id !== colId));
    setActiveColumns(prev => prev.filter(id => id !== colId));
    setColumnFilters(prev => {
      const next = { ...prev };
      delete next[colId];
      return next;
    });
    setColumnWidths(prev => {
      const next = { ...prev };
      delete next[colId];
      return next;
    });
    setRows(prev => prev.map(row => {
      if (!row.customFields || !(colId in row.customFields)) return row;
      const nextCustomFields = { ...row.customFields };
      delete nextCustomFields[colId];
      return { ...row, customFields: nextCustomFields };
    }));
    if (sortConfig?.columnId === colId) setSortConfig(null);
    setRenamingCol(null);
    setRenameDraft('');
    setHeaderMenuCol(null);
    setHeaderMenuPosition(null);
  };

  // ---- Row management ----

  const addSchool = useCallback(() => {
    if (!schoolInfo || typeof schoolInfo !== 'object' || !Object.keys(schoolInfo).length) {
      setSchoolActionMessage('Search for a college, choose a match, then press Add.');
      return;
    }

    // Prevent duplicate colleges
    const newName = schoolInfo['latest.school.name'];
    if (newName) {
      const alreadyExists = rows.some(
        (row) => row.schoolData?.['latest.school.name'] === newName
      );
      if (alreadyExists) {
        setSchoolActionMessage(`${newName} is already in this dashboard.`);
        return;
      }
    }

    // Auto-populate FAFSA code
    const fafsaResult = lookupFafsaCode(newName);
    const autoFields = {};
    if (fafsaResult) autoFields.custom_fafsa_code = fafsaResult.code;

    setRows(prev => [
      ...prev,
      {
        id: Date.now(),
        schoolData: { ...schoolInfo },
        category: '',
        date: '',
        checked: false,
        customFields: { ...autoFields },
      },
    ]);
    setSchoolActionMessage(`${newName || 'College'} added to your dashboard.`);
    dispatchTourEvent(TOUR_COLLEGE_ADDED_EVENT);
  }, [schoolInfo, rows, setRows]);

  const addSchoolByName = useCallback(async (schoolName) => {
    const trimmedName = schoolName?.trim();
    if (!trimmedName) {
      setSchoolActionMessage('Search for a college first.');
      return;
    }
    if (!collegeData) {
      setSchoolActionMessage('College data is still loading. Try again in a moment.');
      return;
    }

    try {
      const data = await smartSearchCollegeDatabase(trimmedName, collegeData);
      if (!data || typeof data !== 'object' || !Object.keys(data).length) {
        setSchoolActionMessage('College not found. Try the full school name.');
        return;
      }

      const newName = data['latest.school.name'];
      if (!newName) {
        setSchoolActionMessage('College not found. Try the full school name.');
        return;
      }

      // Auto-populate FAFSA code
      const fafsaResult = lookupFafsaCode(newName);
      const autoFields = {};
      if (fafsaResult) autoFields.custom_fafsa_code = fafsaResult.code;

      setRows(prev => {
        const alreadyExists = prev.some(
          (row) => row.schoolData?.['latest.school.name'] === newName
        );
        if (alreadyExists) {
          setSchoolActionMessage(`${newName} is already in this dashboard.`);
          return prev;
        }

        setSchoolActionMessage(`${newName} added to your dashboard.`);
        dispatchTourEvent(TOUR_COLLEGE_ADDED_EVENT);
        return [
          ...prev,
          {
            id: Date.now(),
            schoolData: { ...data },
            category: '',
            date: '',
            checked: false,
            customFields: { ...autoFields },
          },
        ];
      });

      setSelectedSchool('');
      setInputValue('');
      setSchoolInfo(null);
      setCollegeNamesList([]);
    } catch (error) {
      console.error('Error adding school:', error);
      setSchoolActionMessage('College not found. Try the full school name.');
    }
  }, [collegeData, setRows]);

  const handleCollegeSearchKeyDown = (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();

    const typedValue = inputValue.trim();
    const liveSuggestions = typedValue && collegeData?.results
      ? getAutocompleteSuggestions(typedValue, collegeData.results, 12)
      : [];
    const suggestions = collegeNamesList.length > 0 ? collegeNamesList : liveSuggestions;
    const exactSuggestion = suggestions.find(
      (name) => name.toLowerCase() === typedValue.toLowerCase()
    );
    const schoolName = selectedSchool || exactSuggestion || suggestions[0] || typedValue;
    if (schoolName) dispatchTourEvent(TOUR_SEARCH_USED_EVENT);
    addSchoolByName(schoolName);
  };

  const deleteRow = (rowId) => {
    setRows(prev => prev.filter(row => row.id !== rowId));
  };

  const updateRowField = (rowId, field, value) => {
    setRows(prev =>
      prev.map(row => (row.id === rowId ? { ...row, [field]: value } : row))
    );
  };

  // ---- Sorting ----

  const handleSort = (colId, direction) => {
    if (sortConfig && sortConfig.columnId === colId && sortConfig.direction === direction) {
      setSortConfig(null);
    } else {
      setSortConfig({ columnId: colId, direction });
    }
  };

  const handleFilterChange = (colId, value) => {
    setColumnFilters(prev => {
      const next = { ...prev };
      if (value.trim()) {
        next[colId] = value;
      } else {
        delete next[colId];
      }
      return next;
    });
  };

  const clearColumnFilter = (colId) => {
    setColumnFilters(prev => {
      const next = { ...prev };
      delete next[colId];
      return next;
    });
  };

  const handleToggleFreezeSchoolName = () => {
    setFrozenSchoolName(prev => {
      const next = !prev;
      if (next && activeColumns.includes('school_name')) {
        setActiveColumns(current => ['school_name', ...current.filter(id => id !== 'school_name')]);
      }
      return next;
    });
    setHeaderMenuCol(null);
  };

  const startColumnResize = (e, colId) => {
    e.preventDefault();
    e.stopPropagation();
    resizeStateRef.current = {
      colId,
      startX: e.clientX,
      startWidth: columnWidths[colId] || e.currentTarget.parentElement.offsetWidth,
    };

    const handleMouseMove = (moveEvent) => {
      const state = resizeStateRef.current;
      if (!state) return;
      const nextWidth = Math.max(120, Math.min(420, state.startWidth + moveEvent.clientX - state.startX));
      setColumnWidths(prev => ({ ...prev, [state.colId]: nextWidth }));
    };

    const handleMouseUp = () => {
      resizeStateRef.current = null;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.classList.remove('is-resizing-column');
    };

    document.body.classList.add('is-resizing-column');
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // ---- Inline cell editing ----

  const handleCellDoubleClick = (rowId, colDef, row) => {
    let currentValue;
    if (colDef.id === 'category') {
      currentValue = row.category || '';
    } else if (colDef.id === 'date') {
      currentValue = row.date || '';
    } else if (colDef.type === 'custom') {
      currentValue = row.customFields?.[colDef.id] || '';
    } else if (Object.prototype.hasOwnProperty.call(row.importedValues || {}, colDef.id)) {
      currentValue = row.importedValues[colDef.id] || '';
    } else if (colDef.apiKey) {
      const val = row.schoolData?.[colDef.apiKey];
      currentValue = val != null ? String(val) : '';
    } else {
      return; // checkbox columns, etc.
    }
    setEditingCell({ rowId, colId: colDef.id });
    setEditCellValue(currentValue);
  };

  const commitCellEdit = () => {
    if (!editingCell) return;
    const { rowId, colId } = editingCell;
    const colDef = getColDef(colId, customColumns);

    setRows(prev => prev.map(row => {
      if (row.id !== rowId) return row;

      if (colId === 'category') {
        return { ...row, category: editCellValue };
      }
      if (colId === 'date') {
        return { ...row, date: editCellValue };
      }
      if (colDef?.type === 'custom') {
        return {
          ...row,
          customFields: { ...(row.customFields || {}), [colId]: editCellValue },
        };
      }
      if (colDef && Object.prototype.hasOwnProperty.call(row.importedValues || {}, colId)) {
        return {
          ...row,
          importedValues: { ...(row.importedValues || {}), [colId]: editCellValue },
        };
      }
      if (colDef && colDef.apiKey) {
        return {
          ...row,
          schoolData: { ...row.schoolData, [colDef.apiKey]: editCellValue },
        };
      }
      return row;
    }));

    setEditingCell(null);
    setEditCellValue('');
  };

  const cancelCellEdit = () => {
    setEditingCell(null);
    setEditCellValue('');
  };

  const handleCellKeyDown = (e) => {
    if (e.key === 'Enter') {
      commitCellEdit();
    } else if (e.key === 'Escape') {
      cancelCellEdit();
    }
  };

  const showCustomTextPreview = (text, element) => {
    if (!text) return;

    const contentElement = element.querySelector('.cell-text-content') || element;
    const isClipped =
      contentElement.scrollWidth > contentElement.clientWidth + 1 ||
      contentElement.scrollHeight > contentElement.clientHeight + 1;

    if (!isClipped) {
      setCustomTextPreview(null);
      return;
    }

    const rect = element.getBoundingClientRect();
    const width = Math.min(360, window.innerWidth - 32);
    const left = Math.min(
      Math.max(rect.left + rect.width / 2, width / 2 + 16),
      window.innerWidth - width / 2 - 16
    );
    const showAbove = rect.top > 250;

    setCustomTextPreview({
      text,
      width,
      left,
      top: showAbove ? rect.top - 12 : rect.bottom + 12,
      placement: showAbove ? 'above' : 'below',
    });
  };

  // Focus cell input when editing starts
  useEffect(() => {
    if (editingCell && cellInputRef.current) {
      cellInputRef.current.focus();
      cellInputRef.current.select();
    }
  }, [editingCell]);

  const getDataDisplayValue = useCallback((colDef, row) => {
    if (Object.prototype.hasOwnProperty.call(row.importedValues || {}, colDef.id)) {
      return row.importedValues[colDef.id] || '—';
    }
    return formatValue(colDef, row.schoolData);
  }, []);

  const getFilterText = useCallback((colDef, row) => {
    if (!colDef) return '';
    if (colDef.id === 'checkbox') return row.checked ? 'checked yes complete true' : 'unchecked no incomplete false';
    if (colDef.id === 'category') return row.category || '';
    if (colDef.id === 'date') return row.date || '';
    if (colDef.type === 'custom') {
      const value = row.customFields?.[colDef.id];
      if (colDef.customType === 'checkbox') return value ? 'checked yes true' : 'unchecked no false';
      return value || '';
    }
    return getDataDisplayValue(colDef, row);
  }, [getDataDisplayValue]);

  const filteredRows = useMemo(() => {
    const activeFilters = Object.entries(columnFilters).filter(([, value]) => value?.trim());
    if (activeFilters.length === 0) return rows;

    return rows.filter(row => activeFilters.every(([colId, value]) => {
      const colDef = getColDef(colId, customColumns);
      return getFilterText(colDef, row).toLowerCase().includes(value.trim().toLowerCase());
    }));
  }, [rows, columnFilters, customColumns, getFilterText]);

  const sortedRows = useMemo(() => {
    if (!sortConfig) return filteredRows;

    const colDef = getColDef(sortConfig.columnId, customColumns);
    if (!colDef) return filteredRows;

    const sorted = [...filteredRows].sort((a, b) => {
      let valA = Object.prototype.hasOwnProperty.call(a.importedValues || {}, colDef.id)
        ? a.importedValues[colDef.id]
        : getRawSortValue(colDef, a);
      let valB = Object.prototype.hasOwnProperty.call(b.importedValues || {}, colDef.id)
        ? b.importedValues[colDef.id]
        : getRawSortValue(colDef, b);

      return compareColumnSortValues(valA, valB, colDef, sortConfig.direction);
    });
    return sorted;
  }, [filteredRows, sortConfig, customColumns]);

  const exportColumns = useMemo(() => activeColumns
    .map((columnId) => getColDef(columnId, customColumns))
    .filter(Boolean), [activeColumns, customColumns]);

  const getExportValue = useCallback((column, row) => {
    if (column.id === 'checkbox') return row.checked ? 'Complete' : 'Not complete';
    if (column.id === 'category') return row.category || '';
    if (column.id === 'date') return row.date || '';
    if (column.type === 'custom') {
      const value = row.customFields?.[column.id];
      if (column.customType === 'checkbox') return value ? 'Yes' : 'No';
      return value ?? '';
    }
    const displayValue = getDataDisplayValue(column, row);
    return displayValue === '—' ? '' : displayValue;
  }, [getDataDisplayValue]);

  const safeDashboardName = (dashboardName || 'College Dashboard')
    .replace(/[^a-z0-9-_]+/gi, '-')
    .replace(/^-+|-+$/g, '') || 'college-dashboard';

  const buildExportTableHtml = useCallback((includeDocument = false) => {
    const header = exportColumns.map((column) => `<th>${escapeHtml(column.label)}</th>`).join('');
    const body = sortedRows.map((row) => {
      const cells = exportColumns.map((column) => {
        const value = getExportValue(column, row);
        const isLink = column.customType === 'link' || column.format === 'url';
        const href = isLink ? normalizeLinkHref(value) : '';
        return `<td>${href ? `<a href="${escapeHtml(href)}">${escapeHtml(value)}</a>` : escapeHtml(value)}</td>`;
      }).join('');
      return `<tr>${cells}</tr>`;
    }).join('');
    const table = `<table><thead><tr>${header}</tr></thead><tbody>${body}</tbody></table>`;
    if (!includeDocument) return table;
    return `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(dashboardName || 'College Dashboard')}</title><style>@page{size:landscape;margin:0.45in}body{font-family:Arial,sans-serif;color:#181818}h1{font-size:18px;margin:0 0 12px}p{font-size:10px;color:#666;margin:0 0 12px}table{width:100%;border-collapse:collapse;table-layout:auto}th,td{border:1px solid #d7d7d7;padding:5px 6px;text-align:left;vertical-align:top;font-size:8px;overflow-wrap:anywhere}th{background:#f1f3f5;font-weight:700}tr{break-inside:avoid}a{color:#1466c3}</style></head><body><h1>${escapeHtml(dashboardName || 'College Dashboard')}</h1><p>${sortedRows.length} ${sortedRows.length === 1 ? 'college' : 'colleges'} · Exported ${escapeHtml(new Date().toLocaleDateString())}</p>${table}</body></html>`;
  }, [dashboardName, exportColumns, getExportValue, sortedRows]);

  const handleExportCsv = () => {
    const lines = [
      exportColumns.map((column) => escapeCsv(column.label)).join(','),
      ...sortedRows.map((row) => exportColumns.map((column) => escapeCsv(getExportValue(column, row))).join(',')),
    ];
    downloadFile(`\uFEFF${lines.join('\r\n')}`, `${safeDashboardName}.csv`, 'text/csv;charset=utf-8');
    setExportOpen(false);
  };

  const handleExportExcel = () => {
    const headers = exportColumns.map((column) => column.label);
    const exportRows = sortedRows.map((row) => exportColumns.map((column) => getExportValue(column, row)));
    const linkColumns = new Set(exportColumns
      .map((column, index) => (column.customType === 'link' || column.format === 'url' ? index : null))
      .filter((index) => index !== null));
    const workbook = createXlsxFile(headers, exportRows, linkColumns);
    downloadFile(workbook, `${safeDashboardName}.xlsx`, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
    setExportOpen(false);
  };

  const handleExportPdf = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    printWindow.opener = null;
    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
    };
    printWindow.document.open();
    printWindow.document.write(buildExportTableHtml(true));
    printWindow.document.close();
    setExportOpen(false);
  };

  const defaultColumnWidth = tableExpanded ? 128 : 160;
  const tableMinWidth = activeColumns.reduce((total, colId) => total + (columnWidths[colId] || defaultColumnWidth), 0);
  const activeHeaderMenuColDef = headerMenuCol ? getColDef(headerMenuCol, customColumns) : null;
  const isHeaderMenuUserCustomColumn = activeHeaderMenuColDef?.type === 'custom' && !activeHeaderMenuColDef?.isBuiltIn;
  const pageClassName = `college-list-page${tableExpanded ? ' college-list-page--table-expanded' : ''}`;
  const collegeNoOptionsText = collegeLoadError
    ? "Colleges couldn't load. Try refreshing the page."
    : inputValue.trim()
      ? 'College not found'
      : 'Start typing to search colleges';

  // ---- Render ----

  return (
    <div className={pageClassName}>
      <div className="college-list-page__header">
        <h1 className="college-list-page__title">{dashboardName || 'College List'}</h1>
        <p className="college-list-page__subtitle">
          Dashboards are for researching, comparing, and organizing college lists. When you know which schools you are actually applying to, use Progress as your final application list.
        </p>
      </div>

      <div className="college-list-page__controls">
        <div data-tour="college-search">
          <Autocomplete
            options={collegeNamesList}
            loading={collegeLoading}
            loadingText="Loading colleges..."
            noOptionsText={collegeNoOptionsText}
            inputValue={inputValue}
            onInputChange={(event, newInputValue) => {
              setInputValue(newInputValue);
              setSchoolActionMessage('');
              if (!newInputValue) setSelectedSchool('');
            }}
            onChange={(event, newValue) => {
              setSelectedSchool(newValue || '');
              setInputValue(newValue || '');
              setSchoolActionMessage('');
              if (newValue) dispatchTourEvent(TOUR_SEARCH_USED_EVENT);
            }}
            filterOptions={(x) => x}
            sx={{
              width: 340,
              '& .MuiOutlinedInput-root': {
                fontFamily: "var(--ds-font-family, 'Poppins', sans-serif)",
                fontSize: '0.88rem',
                borderRadius: '10px',
                backgroundColor: 'var(--ds-surface)',
                color: 'var(--ds-primary-black)',
                padding: '3px 10px',
                transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                '& fieldset': {
                  borderColor: 'var(--ds-border)',
                  borderWidth: '1px',
                  transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                },
                '&:hover fieldset': {
                  borderColor: 'var(--ds-primary-300)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'var(--ds-active-color)',
                  borderWidth: '1.5px',
                  boxShadow: '0 0 0 3px rgba(43, 122, 232, 0.08)',
                },
              },
              '& .MuiInputBase-input': {
                fontFamily: "var(--ds-font-family, 'Poppins', sans-serif)",
                fontSize: '0.88rem',
                color: 'var(--ds-primary-black)',
                '&::placeholder': {
                  color: 'var(--ds-text-muted)',
                  opacity: 1,
                },
              },
              '& .MuiAutocomplete-popupIndicator': {
                color: 'var(--ds-text-muted)',
              },
              '& .MuiAutocomplete-clearIndicator': {
                color: 'var(--ds-text-muted)',
              },
            }}
            slotProps={{
              paper: {
                sx: {
                  fontFamily: "var(--ds-font-family, 'Poppins', sans-serif)",
                  fontSize: '0.88rem',
                  borderRadius: '10px',
                  border: '1px solid var(--ds-border)',
                  boxShadow: '0 8px 30px rgba(0, 0, 0, 0.08)',
                  backgroundColor: 'var(--ds-surface)',
                  marginTop: '6px',
                  '& .MuiAutocomplete-listbox': {
                    padding: '6px',
                    '& .MuiAutocomplete-option': {
                      fontFamily: "var(--ds-font-family, 'Poppins', sans-serif)",
                      fontSize: '0.88rem',
                      borderRadius: '6px',
                      padding: '8px 12px',
                      color: 'var(--ds-primary-black)',
                      '&:hover': {
                        backgroundColor: 'var(--ds-surface-hover)',
                      },
                      '&[aria-selected="true"]': {
                        backgroundColor: 'var(--ds-active-bg)',
                        color: 'var(--ds-active-color)',
                      },
                    },
                  },
                  '& .MuiAutocomplete-noOptions': {
                    fontFamily: "var(--ds-font-family, 'Poppins', sans-serif)",
                    fontSize: '0.85rem',
                    color: 'var(--ds-text-muted)',
                  },
                  '& .MuiAutocomplete-loading': {
                    fontFamily: "var(--ds-font-family, 'Poppins', sans-serif)",
                    fontSize: '0.85rem',
                    color: 'var(--ds-text-muted)',
                  },
                },
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search for a college"
                onKeyDown={handleCollegeSearchKeyDown}
              />
            )}
          />
        </div>
        <button className="college-list-page__add-btn" data-tour="add-college" onClick={addSchool}>Add</button>
        {schoolActionMessage && (
          <p className="college-list-page__action-message" role="status" aria-live="polite">
            {schoolActionMessage}
          </p>
        )}

        {/* Multi-select column picker */}
        <div className="column-picker-wrapper" ref={pickerRef}>
          <button
            className="college-list-page__columns-btn"
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const gap = 8;
              const viewportPadding = 12;
              const spaceBelow = window.innerHeight - rect.bottom - gap - viewportPadding;
              const spaceAbove = rect.top - gap - viewportPadding;
              const openAbove = spaceBelow < 280 && spaceAbove > spaceBelow;
              const availableHeight = Math.max(180, openAbove ? spaceAbove : spaceBelow);

              setColumnPickerPos({
                ...(openAbove
                  ? { bottom: window.innerHeight - rect.top + gap }
                  : { top: rect.bottom + gap }),
                right: Math.max(viewportPadding, window.innerWidth - rect.right),
                maxHeight: Math.min(460, availableHeight),
              });
              setColumnPickerOpen((prev) => !prev);
            }}
          >
            <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M2 3.5h11M2 7.5h11M2 11.5h11" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
            </svg>
            Columns
            <span className="column-picker-caret">{columnPickerOpen ? '▲' : '▼'}</span>
          </button>

          {columnPickerOpen && (
            <ColumnPicker
              activeColumns={activeColumns}
              customColumns={customColumns}
              onToggleColumn={toggleColumn}
              onCreateCustomColumn={handleCreateCustomColumn}
              searchValue={columnSearch}
              onSearchChange={setColumnSearch}
              style={columnPickerPos ? {
                position: 'fixed',
                ...(columnPickerPos.top !== undefined ? { top: columnPickerPos.top } : {}),
                ...(columnPickerPos.bottom !== undefined ? { bottom: columnPickerPos.bottom } : {}),
                right: columnPickerPos.right,
                maxHeight: columnPickerPos.maxHeight,
                zIndex: 1000,
              } : undefined}
            />
          )}
        </div>

        <div className="college-list-page__export" ref={exportRef}>
          <button
            type="button"
            className="college-list-page__export-btn"
            onClick={() => setExportOpen((previous) => !previous)}
            aria-expanded={exportOpen}
          >
            <Download size={15} strokeWidth={1.9} />
            Export
            <ChevronDown size={13} strokeWidth={1.9} />
          </button>
          {exportOpen && (
            <div className="college-list-page__export-menu">
              <button type="button" onClick={handleExportExcel}>
                <strong>Excel</strong>
                <span>Open in Excel, Sheets, or Numbers</span>
              </button>
              <button type="button" onClick={handleExportCsv}>
                <strong>CSV</strong>
                <span>Simple spreadsheet file</span>
              </button>
              <button type="button" onClick={handleExportPdf}>
                <strong>PDF</strong>
                <span>Print or save a landscape copy</span>
              </button>
            </div>
          )}
        </div>

        <button
          type="button"
          className="college-list-page__expand-btn"
          onClick={() => setTableExpanded((prev) => !prev)}
          aria-pressed={tableExpanded}
        >
          {tableExpanded ? <Minimize2 size={15} strokeWidth={1.9} /> : <Maximize2 size={15} strokeWidth={1.9} />}
          {tableExpanded ? 'Minimize Table' : 'Expand Table'}
        </button>
      </div>

      {/* Sort/filter indicator */}
      {(sortConfig || Object.keys(columnFilters).length > 0) && (
        <div className="sort-indicator">
          {sortConfig && (
            <span>
              Sorted by <strong>{getColDef(sortConfig.columnId, customColumns)?.label}</strong>{' '}
              ({sortConfig.direction === 'asc' ? '↑ Ascending' : '↓ Descending'})
            </span>
          )}
          {Object.keys(columnFilters).length > 0 && (
            <span>
              {Object.keys(columnFilters).length} filter{Object.keys(columnFilters).length === 1 ? '' : 's'} on
            </span>
          )}
          <button
            className="clear-sort-btn"
            onClick={() => {
              setSortConfig(null);
              setColumnFilters({});
            }}
          >
            ✕ Clear
          </button>
        </div>
      )}

      {/* <div className="college-list-page__progress">
        <progress id="idProgressBar" value={progressValue} />
      </div> */}

      <div className="college-list-page__table-wrapper">
        <table
          className="college-list-page__table"
          id="collegeTable"
          style={{ minWidth: `${tableMinWidth}px` }}
        >
          <thead>
            <tr>
              {activeColumns.map((colId) => {
                const colDef = getColDef(colId, customColumns);
                if (!colDef) return null;

                const isActive = sortConfig && sortConfig.columnId === colId;
                const activeDir = isActive ? sortConfig.direction : null;
                const isFrozenSchoolName = frozenSchoolName && colId === 'school_name';
                const headerClasses = [
                  'sortable-header',
                  draggedCol === colId ? 'sortable-header--dragging' : '',
                  dropTargetCol === colId ? 'sortable-header--drop-target' : '',
                  columnFilters[colId] ? 'sortable-header--filtered' : '',
                  isFrozenSchoolName ? 'col--frozen col--frozen-header' : '',
                ].filter(Boolean).join(' ');
                const colStyle = columnWidths[colId]
                  ? { width: `${columnWidths[colId]}px`, minWidth: `${columnWidths[colId]}px` }
                  : { width: `${defaultColumnWidth}px`, minWidth: `${defaultColumnWidth}px` };

                return (
                  <th
                    key={colId}
                    className={headerClasses}
                    style={colStyle}
                    draggable
                    onDragStart={(e) => {
                      setDraggedCol(colId);
                      e.dataTransfer.effectAllowed = 'move';
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'move';
                    }}
                    onDragEnter={(e) => {
                      e.preventDefault();
                      if (draggedCol && draggedCol !== colId) {
                        setDropTargetCol(colId);
                      }
                    }}
                    onDragLeave={() => {
                      if (dropTargetCol === colId) setDropTargetCol(null);
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      setDropTargetCol(null);
                      if (draggedCol && draggedCol !== colId) {
                        setActiveColumns(prev => {
                          const updated = [...prev];
                          const fromIdx = updated.indexOf(draggedCol);
                          const toIdx = updated.indexOf(colId);
                          if (fromIdx === -1 || toIdx === -1) return prev;
                          updated.splice(fromIdx, 1);
                          updated.splice(toIdx, 0, draggedCol);
                          return updated;
                        });
                      }
                      setDraggedCol(null);
                    }}
                    onDragEnd={() => { setDraggedCol(null); setDropTargetCol(null); }}
                  >
                    <div className="header-content">
                      <span className="header-drag-handle" title="Drag to reorder">⠿</span>
                      <span className="header-label">{colDef.label}</span>
                      {activeDir && <span className="header-sort-badge">{activeDir === 'asc' ? '↑' : '↓'}</span>}
                      {columnFilters[colId] && <span className="header-filter-badge">Filtered</span>}
                      <button
                        type="button"
                        className="column-menu-trigger"
                        aria-expanded={headerMenuCol === colId}
                        onClick={(e) => {
                          e.stopPropagation();
                          setRenamingCol(null);
                          setRenameDraft('');
                          const rect = e.currentTarget.getBoundingClientRect();
                          const menuWidth = 220;
                          const left = Math.max(12, Math.min(window.innerWidth - menuWidth - 12, rect.right - menuWidth));
                          setHeaderMenuPosition({ top: rect.bottom + 8, left });
                          setHeaderMenuCol(prev => {
                            if (prev === colId) {
                              setHeaderMenuPosition(null);
                              return null;
                            }
                            return colId;
                          });
                        }}
                        title={`${colDef.label} options`}
                      >
                        ⋯
                      </button>
                    </div>
                    <span
                      className="column-resize-handle"
                      onMouseDown={(e) => startColumnResize(e, colId)}
                      title="Resize column"
                    />
                  </th>
                );
              })}
              <th className="row-action-header"></th>
            </tr>
          </thead>

          <tbody>
            {sortedRows.length === 0 ? (
              <tr>
                <td colSpan={activeColumns.length + 1} className="college-list-page__empty-state">
                  <div className="college-list-page__empty-content">
                    <p>{rows.length === 0 ? 'Start by adding one college' : 'No colleges match these filters'}</p>
                    <p className="college-list-page__empty-hint">
                      {rows.length === 0
                        ? 'Search above, choose a school, and click Add. One school is enough to start comparing.'
                        : 'Clear filters or adjust the column filter text'}
                    </p>
                    {rows.length === 0 && (
                      <div className="college-list-page__empty-actions" aria-label="Starter college examples">
                        {STARTER_COLLEGE_EXAMPLES.map((schoolName) => (
                          <button
                            type="button"
                            key={schoolName}
                            onClick={() => addSchoolByName(schoolName)}
                            disabled={collegeLoading}
                          >
                            Add {schoolName}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ) : (
              sortedRows.map(row => (
                <tr key={row.id} className="table-row">
                  {activeColumns.map(colId => {
                    const colDef = getColDef(colId, customColumns);
                    if (!colDef) return null;

                    const isFrozenSchoolName = frozenSchoolName && colId === 'school_name';
                    const colDragClass = `${draggedCol === colId ? ' col--dragging' : ''}${dropTargetCol === colId ? ' col--drop-target' : ''}${isFrozenSchoolName ? ' col--frozen' : ''}`;
                    const colStyle = columnWidths[colId]
                      ? { width: `${columnWidths[colId]}px`, minWidth: `${columnWidths[colId]}px` }
                      : { width: `${defaultColumnWidth}px`, minWidth: `${defaultColumnWidth}px` };

                    if (colDef.type === 'checkbox') {
                      return (
                        <td key={colId} className={colDragClass} style={colStyle}>
                          <input
                            type="checkbox"
                            className="checkedCollege"
                            checked={row.checked}
                            onChange={(e) => updateRowField(row.id, 'checked', e.target.checked)}
                          />
                        </td>
                      );
                    }

                    // Inline editing for any non-checkbox cell
                    const isEditing = editingCell && editingCell.rowId === row.id && editingCell.colId === colId;

                    if (isEditing) {
                      return (
                        <td key={colId} className={colDragClass} style={colStyle}>
                          <input
                            ref={cellInputRef}
                            className="cell-edit-input"
                            type={colDef.type === 'date' || colDef.customType === 'date' ? 'date' : colDef.customType === 'link' || colDef.format === 'url' ? 'url' : 'text'}
                            value={editCellValue}
                            onChange={(e) => setEditCellValue(e.target.value)}
                            onKeyDown={handleCellKeyDown}
                            onBlur={commitCellEdit}
                          />
                        </td>
                      );
                    }

                    if (colDef.type === 'select') {
                      const catClass = row.category ? `category-tag category-tag--${row.category.toLowerCase()}` : 'category-tag category-tag--empty';
                      return (
                        <td key={colId} className={colDragClass} style={colStyle}>
                          <select
                            className={catClass}
                            value={row.category}
                            onChange={(e) => updateRowField(row.id, 'category', e.target.value)}
                          >
                            <option value="" disabled>Select</option>
                            {CATEGORY_OPTIONS.map(opt => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        </td>
                      );
                    }

                    if (colDef.type === 'date') {
                      return (
                        <td key={colId} className={colDragClass} style={colStyle} onDoubleClick={() => handleCellDoubleClick(row.id, colDef, row)}>
                          <input
                            type="date"
                            value={row.date}
                            onChange={(e) => updateRowField(row.id, 'date', e.target.value)}
                          />
                        </td>
                      );
                    }

                    if (colDef.type === 'custom') {
                      const customValue = row.customFields?.[colId] || '';
                      if (colDef.customType === 'checkbox') {
                        return (
                          <td key={colId} className={colDragClass} style={colStyle}>
                            <input
                              type="checkbox"
                              className="checkedCollege"
                              checked={Boolean(customValue)}
                              onChange={(e) => updateCustomField(row.id, colId, e.target.checked)}
                            />
                          </td>
                        );
                      }
                      if (colDef.customType === 'date') {
                        return (
                          <td key={colId} className={colDragClass} style={colStyle}>
                            <input
                              type="date"
                              className="custom-cell-input custom-cell-input--date"
                              value={customValue}
                              onChange={(e) => updateCustomField(row.id, colId, e.target.value)}
                            />
                          </td>
                        );
                      }
                      if (colDef.customType === 'number') {
                        return (
                          <td key={colId} className={colDragClass} style={colStyle}>
                            <input
                              type="number"
                              className="custom-cell-input custom-cell-input--number"
                              value={customValue}
                              onChange={(e) => updateCustomField(row.id, colId, e.target.value)}
                            />
                          </td>
                        );
                      }
                      if (colDef.customType === 'link') {
                        const href = normalizeLinkHref(customValue);
                        return (
                          <td
                            key={colId}
                            className={`cell-editable custom-link-cell${customValue ? ' custom-link-cell--has-value' : ''}${colDragClass}`}
                            style={colStyle}
                            onDoubleClick={() => handleCellDoubleClick(row.id, colDef, row)}
                          >
                            {customValue ? (
                              <a href={href} target="_blank" rel="noreferrer" onDoubleClick={(e) => e.preventDefault()}>
                                Open link
                              </a>
                            ) : (
                              <span>Add link</span>
                            )}
                          </td>
                        );
                      }
                      if (colDef.customType === 'dropdown') {
                        return (
                          <td key={colId} className={colDragClass} style={colStyle}>
                            <select
                              className="custom-dropdown-select"
                              value={customValue}
                              onChange={(e) => updateCustomField(row.id, colId, e.target.value)}
                            >
                              <option value="">Select</option>
                              {(colDef.labels || []).map((label) => (
                                <option key={label} value={label}>{label}</option>
                              ))}
                            </select>
                          </td>
                        );
                      }
                      if (colDef.customType === 'label') {
                        return (
                          <td key={colId} className={colDragClass} style={colStyle}>
                            <select
                              className={getLabelSelectClass(customValue)}
                              value={customValue}
                              onChange={(e) => updateCustomField(row.id, colId, e.target.value)}
                            >
                              <option value="">Select</option>
                              {(colDef.labels || []).map((label) => (
                                <option key={label} value={label}>{label}</option>
                              ))}
                            </select>
                          </td>
                        );
                      }
                      return (
                        <td
                          key={colId}
                          className={`cell-editable cell-editable--custom custom-text-cell${customValue ? ' custom-text-cell--has-value' : ''}${colDragClass}`}
                          style={colStyle}
                          title="Double-click to edit"
                          tabIndex={customValue ? 0 : undefined}
                          onMouseEnter={(e) => showCustomTextPreview(customValue, e.currentTarget)}
                          onMouseLeave={() => setCustomTextPreview(null)}
                          onFocus={(e) => showCustomTextPreview(customValue, e.currentTarget)}
                          onBlur={() => setCustomTextPreview(null)}
                          onDoubleClick={() => handleCellDoubleClick(row.id, colDef, row)}
                        >
                          {customValue ? (
                            <pre className="cell-text-content">{customValue}</pre>
                          ) : (
                            <span className="custom-text-cell__edit-hint">Double-click to add</span>
                          )}
                        </td>
                      );
                    }

                    const displayValue = getDataDisplayValue(colDef, row);
                    const showPreview = displayValue && displayValue !== '—';
                    if (colDef.format === 'url') {
                      const href = normalizeLinkHref(displayValue);
                      return (
                        <td
                          key={colId}
                          className={`cell-editable custom-link-cell${showPreview ? ' custom-link-cell--has-value' : ''}${colDragClass}`}
                          style={colStyle}
                          onDoubleClick={() => handleCellDoubleClick(row.id, colDef, row)}
                        >
                          {showPreview ? (
                            <a href={href} target="_blank" rel="noreferrer" onDoubleClick={(e) => e.preventDefault()}>
                              Website
                            </a>
                          ) : (
                            <span>Add link</span>
                          )}
                        </td>
                      );
                    }
                    return (
                      <td
                        key={colId}
                        className={`cell-editable custom-text-cell${showPreview ? ' custom-text-cell--has-value' : ''}${colDragClass}`}
                        style={colStyle}
                        tabIndex={showPreview ? 0 : undefined}
                        onMouseEnter={(e) => showCustomTextPreview(displayValue, e.currentTarget)}
                        onMouseLeave={() => setCustomTextPreview(null)}
                        onFocus={(e) => showCustomTextPreview(displayValue, e.currentTarget)}
                        onBlur={() => setCustomTextPreview(null)}
                        onDoubleClick={() => handleCellDoubleClick(row.id, colDef, row)}
                      >
                        <pre className="cell-text-content">{displayValue}</pre>
                      </td>
                    );
                  })}

                  <td className="row-delete-cell">
                    <button
                      className="delete-row-btn"
                      onClick={() => deleteRow(row.id)}
                      title="Delete row"
                    >
                      ×
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {headerMenuCol && activeHeaderMenuColDef && headerMenuPosition && (
        <div
          className="column-header-menu"
          style={headerMenuPosition}
          onClick={(e) => e.stopPropagation()}
        >
          {activeHeaderMenuColDef.sortable && (
            <>
              <button type="button" onClick={() => handleSort(headerMenuCol, 'asc')}>
                {activeHeaderMenuColDef.sortType === 'number' ? 'Sort smallest first' : 'Sort A to Z'}
              </button>
              <button type="button" onClick={() => handleSort(headerMenuCol, 'desc')}>
                {activeHeaderMenuColDef.sortType === 'number' ? 'Sort largest first' : 'Sort Z to A'}
              </button>
            </>
          )}
          <label className="column-filter-control">
            <span>Filter</span>
            <input
              type="text"
              value={columnFilters[headerMenuCol] || ''}
              onChange={(e) => handleFilterChange(headerMenuCol, e.target.value)}
              placeholder={`Find ${activeHeaderMenuColDef.label}`}
            />
          </label>
          {columnFilters[headerMenuCol] && (
            <button type="button" onClick={() => clearColumnFilter(headerMenuCol)}>
              Clear filter
            </button>
          )}
          {headerMenuCol === 'school_name' && (
            <button type="button" onClick={handleToggleFreezeSchoolName}>
              {frozenSchoolName ? 'Unfreeze School Name' : 'Freeze School Name'}
            </button>
          )}
          <button type="button" onClick={() => handleRemoveColumnFromTable(headerMenuCol)}>
            Remove from table
          </button>
          {isHeaderMenuUserCustomColumn && (
            <>
              <div className="column-header-menu__divider" />
              {renamingCol === headerMenuCol ? (
                <form
                  className="column-rename-form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    saveRenameCustomColumn(headerMenuCol);
                  }}
                >
                  <input
                    type="text"
                    value={renameDraft}
                    onChange={(e) => setRenameDraft(e.target.value)}
                    autoFocus
                  />
                  <div className="column-rename-form__actions">
                    <button type="submit">Save</button>
                    <button
                      type="button"
                      onClick={() => {
                        setRenamingCol(null);
                        setRenameDraft('');
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <button type="button" onClick={() => startRenameCustomColumn(headerMenuCol)}>
                  Rename field
                </button>
              )}
              <button type="button" className="column-header-menu__danger" onClick={() => handleDeleteCustomColumn(headerMenuCol)}>
                Delete field
              </button>
            </>
          )}
        </div>
      )}

      {customTextPreview && (
        <div
          className={`custom-text-cell-floating-preview custom-text-cell-floating-preview--${customTextPreview.placement}`}
          style={{
            left: customTextPreview.left,
            top: customTextPreview.top,
            width: customTextPreview.width,
          }}
          role="tooltip"
        >
          {customTextPreview.text}
        </div>
      )}
    </div>
  );
}

export default CollegeListPage;
